import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';

import { readRecentBoardIds } from '@/entities/board';
import { useMyBoards } from '@/entities/collab';
import { useViewer } from '@/entities/viewer';
import { readRecentDatabaseIds, useMyDatabases } from '@/entities/database';
import { useWorkspaces } from '@/entities/workspace';
import { DatabaseList } from '@/features/database-catalog';
import { HubNav } from '@/features/hub-nav';
import { Combobox, EmptyState, ErrorState, LoadingState, MultiFilter } from '@/shared/ui';

import { type BoardSort, sortBoards } from './board-sort';
import { isPersonalBoard } from './board-groups';
import { BoardCard, WorkspaceCard } from './Cards';
import styles from './HomeDashboard.module.css';

export type Category = 'boards' | 'team-boards' | 'workspaces' | 'databases' | 'favorites';
type Relation = 'all' | 'owned' | 'shared';
type Scope = 'all' | 'personal' | 'team';
const SCOPES: readonly Scope[] = ['all', 'personal', 'team'];

/**
 * Full-screen list of one category with search and filters (relation, workspace type, workspace,
 * labels, sort). Searching and filtering happen on data the viewer is already allowed to see (the
 * server decided that). `?q=` and `?space=` (all / personal / team) carry the search text and the
 * workspace type, so a link from Home's quick results or a bookmark lands on the same list.
 */
export function CategoryView({ category }: { category: Category }) {
  const { t, i18n } = useTranslation();
  const language = i18n.language;
  const [params, setParams] = useSearchParams();
  const [query, setQuery] = useState(params.get('q') ?? '');
  const [relation, setRelation] = useState<Relation>('all');
  const urlScope = params.get('space');
  const scope: Scope = SCOPES.find((s) => s === urlScope) ?? 'all';
  const setScope = (next: Scope) => {
    setParams(
      (current) => {
        const copy = new URLSearchParams(current);
        if (next === 'all') copy.delete('space');
        else copy.set('space', next);
        return copy;
      },
      { replace: true },
    );
  };
  const [workspaceId, setWorkspaceId] = useState<string | null>(null);
  const [onlyFavorites, setOnlyFavorites] = useState(false);
  const viewer = useViewer();
  const userId = viewer.status === 'authenticated' ? viewer.user.id : null;
  const openedIds = useMemo(() => (userId ? readRecentBoardIds(userId) : []), [userId]);
  const openedDatabaseIds = useMemo(() => (userId ? readRecentDatabaseIds(userId) : []), [userId]);
  const [chosenSort, setChosenSort] = useState<BoardSort | null>(null);
  // Useful by default: what was opened lately, else what changed lately.
  const sort: BoardSort =
    chosenSort ??
    ((category === 'databases' ? openedDatabaseIds : openedIds).length > 0 ? 'opened' : 'updated');
  // several labels are ORed (a board with ANY of them passes); the other filters are ANDed
  const [labelIds, setLabelIds] = useState<string[]>([]);
  const databasesCategory = category === 'databases';
  const boards = useMyBoards({ limit: 1000 });
  const databases = useMyDatabases(databasesCategory);
  const workspaces = useWorkspaces();
  const needle = query.trim().toLocaleLowerCase();
  const title = t(
    category === 'boards'
      ? 'nav.myBoards'
      : category === 'team-boards'
        ? 'nav.teamBoards'
        : category === 'workspaces'
          ? 'nav.workspaces'
          : category === 'databases'
            ? 'nav.myDatabases'
            : 'nav.favorites',
  );
  const personalLabel = t('home.personalWorkspace');
  const labelOptions = useMemo(() => {
    const seen = new Map<string, { value: string; label: string; color: string }>();
    for (const board of boards.data ?? []) {
      for (const label of board.labels) {
        seen.set(label.id, { value: label.id, label: label.name, color: label.color });
      }
    }
    return [...seen.values()].sort((a, b) => a.label.localeCompare(b.label, language));
  }, [boards.data, language]);
  // a label that was deleted (or lost by every board) drops out of the selection
  const activeLabelIds = boards.data
    ? labelIds.filter((id) => labelOptions.some((option) => option.value === id))
    : labelIds;
  const spaceOf = useMemo(
    () => new Map((workspaces.data ?? []).map((w) => [w.id, w.workspace_type] as const)),
    [workspaces.data],
  );
  const nameOf = useMemo(
    () =>
      new Map(
        (workspaces.data ?? []).map((w) => [
          w.id,
          w.workspace_type === 'personal' ? personalLabel : w.name,
        ]),
      ),
    [workspaces.data, personalLabel],
  );

  const boardItems = useMemo(() => {
    const list = (boards.data ?? [])
      .filter((b) => category !== 'favorites' || b.is_favorite)
      .filter((b) => category !== 'team-boards' || !isPersonalBoard(b))
      .filter((b) => scope === 'all' || isPersonalBoard(b) === (scope === 'personal'))
      .filter((b) => workspaceId === null || b.workspace_id === workspaceId)
      .filter(
        (b) => activeLabelIds.length === 0 || b.labels.some((l) => activeLabelIds.includes(l.id)),
      )
      .filter(
        (b) => relation === 'all' || b.relation === (relation === 'owned' ? 'owned' : 'shared'),
      )
      .filter(
        (b) =>
          !needle ||
          b.name.toLocaleLowerCase().includes(needle) ||
          (b.workspace_name ?? '').toLocaleLowerCase().includes(needle),
      );
    return sortBoards(list, sort, openedIds, language);
  }, [
    boards.data,
    category,
    scope,
    workspaceId,
    relation,
    needle,
    sort,
    activeLabelIds,
    language,
    openedIds,
  ]);

  const workspaceItems = useMemo(() => {
    const list = (workspaces.data ?? [])
      .filter((w) => scope === 'all' || w.workspace_type === scope)
      .filter((w) => relation === 'all' || w.is_owner === (relation === 'owned'))
      .filter(
        (w) =>
          !needle ||
          w.name.toLocaleLowerCase().includes(needle) ||
          (w.workspace_type === 'personal' && personalLabel.toLocaleLowerCase().includes(needle)),
      );
    // the personal workspace first, then the rest (by name when asked)
    const ordered =
      sort === 'name' ? [...list].sort((a, b) => a.name.localeCompare(b.name, language)) : list;
    return [
      ...ordered.filter((w) => w.workspace_type === 'personal'),
      ...ordered.filter((w) => w.workspace_type !== 'personal'),
    ];
  }, [workspaces.data, scope, relation, needle, sort, language, personalLabel]);

  const databaseItems = useMemo(() => {
    const list = (databases.data ?? [])
      .filter((d) => scope === 'all' || (spaceOf.get(d.workspace_id) ?? 'team') === scope)
      .filter((d) => workspaceId === null || d.workspace_id === workspaceId)
      .filter((d) => !onlyFavorites || d.is_favorite)
      .filter((d) => relation === 'all' || (d.owner_user_id === userId) === (relation === 'owned'))
      .filter(
        (d) =>
          !needle ||
          d.name.toLocaleLowerCase().includes(needle) ||
          (nameOf.get(d.workspace_id) ?? '').toLocaleLowerCase().includes(needle),
      );
    if (sort === 'name') {
      return list.toSorted(
        (a, b) => a.name.localeCompare(b.name, language) || a.id.localeCompare(b.id),
      );
    }
    if (sort === 'updated') {
      return list.toSorted(
        (a, b) => b.updated_at.localeCompare(a.updated_at) || a.id.localeCompare(b.id),
      );
    }
    const rank = (id: string) => openedDatabaseIds.indexOf(id) + 1 || Number.MAX_SAFE_INTEGER;
    return list.toSorted((a, b) => rank(a.id) - rank(b.id) || a.id.localeCompare(b.id));
  }, [
    databases.data,
    scope,
    spaceOf,
    workspaceId,
    onlyFavorites,
    relation,
    userId,
    needle,
    nameOf,
    sort,
    language,
    openedDatabaseIds,
  ]);

  const workspaceOptions = useMemo(
    () =>
      [...(workspaces.data ?? [])]
        .sort(
          (a, b) =>
            Number(b.workspace_type === 'personal') - Number(a.workspace_type === 'personal') ||
            a.name.localeCompare(b.name, language),
        )
        .map((w) => ({ value: w.id, label: nameOf.get(w.id) ?? w.name })),
    [workspaces.data, nameOf, language],
  );

  const source = category === 'workspaces' ? workspaces : databasesCategory ? databases : boards;
  const pending = source.isPending || (databasesCategory && workspaces.isPending);
  const error = source.error;
  const items =
    category === 'workspaces'
      ? workspaceItems.length
      : databasesCategory
        ? databaseItems.length
        : boardItems.length;
  const showScope = category !== 'team-boards';
  const showWorkspace = category !== 'workspaces';
  return (
    <div className={styles.page}>
      <header className={styles.head}>
        <h1 className={styles.title}>{title}</h1>
      </header>
      <HubNav />
      <div className={styles.toolbar} role="search">
        <input
          type="search"
          value={query}
          aria-label={t('home.search')}
          placeholder={t('home.search')}
          onChange={(event) => {
            setQuery(event.target.value);
          }}
        />
        {showScope && (
          <div className={styles.toolbarField}>
            <Combobox
              label={t('home.scope')}
              value={scope}
              size="sm"
              searchable={false}
              options={[
                { value: 'all', label: t('home.scopeAll') },
                { value: 'personal', label: t('home.scopePersonal') },
                { value: 'team', label: t('home.scopeTeam') },
              ]}
              onChange={(next) => {
                setScope(SCOPES.find((s) => s === next) ?? 'all');
              }}
            />
          </div>
        )}
        {showWorkspace && workspaceOptions.length > 1 && (
          <div className={styles.toolbarField}>
            <Combobox
              label={t('home.workspaceFilter')}
              value={workspaceId ?? '__all__'}
              size="sm"
              searchPlaceholder={t('common.search')}
              emptyText={t('common.noResults')}
              options={[
                { value: '__all__', label: t('home.allWorkspacesOption') },
                ...workspaceOptions,
              ]}
              onChange={(next) => {
                setWorkspaceId(next === '__all__' ? null : next);
              }}
            />
          </div>
        )}
        <div className={styles.toolbarField}>
          <Combobox
            label={t('home.relation')}
            value={relation}
            size="sm"
            options={[
              { value: 'all', label: t('home.relationAll') },
              { value: 'owned', label: t('home.owned') },
              { value: 'shared', label: t('home.shared') },
            ]}
            onChange={(next) => {
              setRelation(next as Relation);
            }}
          />
        </div>
        {databasesCategory && (
          <div className={styles.toolbarField}>
            <Combobox
              label={t('nav.favorites')}
              value={onlyFavorites ? 'fav' : 'all'}
              size="sm"
              searchable={false}
              options={[
                { value: 'all', label: t('home.relationAll') },
                { value: 'fav', label: t('nav.favorites') },
              ]}
              onChange={(next) => {
                setOnlyFavorites(next === 'fav');
              }}
            />
          </div>
        )}
        {!databasesCategory && category !== 'workspaces' && labelOptions.length > 0 && (
          <div className={styles.toolbarField}>
            <MultiFilter
              label={t('home.labelFilter')}
              testId="category-label-filter"
              options={labelOptions}
              selected={activeLabelIds}
              searchFrom={5}
              searchPlaceholder={t('catalog.labelSearch')}
              emptyText={t('catalog.labelNone')}
              unavailableLabel={t('catalog.labelGone')}
              onChange={setLabelIds}
            />
          </div>
        )}
        <div className={styles.toolbarField}>
          <Combobox
            label={t('home.sort')}
            value={sort}
            size="sm"
            options={[
              ...(category === 'workspaces'
                ? [{ value: 'updated', label: t('home.sortDefault') }]
                : [
                    { value: 'opened', label: t('home.sortOpened') },
                    { value: 'updated', label: t('home.sortUpdated') },
                  ]),
              { value: 'name', label: t('home.sortName') },
            ]}
            onChange={(next) => {
              setChosenSort(next === 'name' ? 'name' : next === 'updated' ? 'updated' : 'opened');
            }}
          />
        </div>
      </div>
      {pending && <LoadingState />}
      {error && (
        <ErrorState
          error={error}
          onRetry={() => {
            void source.refetch();
          }}
        />
      )}
      {!pending && !error && items === 0 && (
        <EmptyState
          title={
            databasesCategory && (databases.data ?? []).length === 0
              ? t('home.noDatabases')
              : t('home.nothingFound')
          }
        />
      )}
      {databasesCategory ? (
        !pending && !error && <DatabaseList databases={databaseItems} workspaceNames={nameOf} />
      ) : (
        <ul className={styles.grid}>
          {category === 'workspaces'
            ? workspaceItems.map((w) => <WorkspaceCard key={w.id} workspace={w} />)
            : boardItems.map((b) => <BoardCard key={b.id} board={b} />)}
        </ul>
      )}
    </div>
  );
}
